/* FileDrop — PS.fileDrop equivalent, built on react-dropzone, rendering the
   mockup `.filedrop` markup. Optional sample-template download + selected-file row.

   props:
     accept (e.g. '.csv,.xlsx'), title, hint, maxMB, multiple,
     sampleName, sampleContent, sampleLabel  — show a "Download template" button,
     readContent  — if true, onFile(file, text) gets the file text (FileReader),
     onFile(file, text?), onError(msg, file)
*/
import React, {useCallback, useState} from 'react'
import PropTypes from 'prop-types'
import {useDropzone} from 'react-dropzone'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import {cx, download} from './helpers'
import Badge from './Badge'

export default function FileDrop({
  accept = '.csv,.xlsx',
  title = 'Drag & drop your file here',
  hint,
  maxMB,
  multiple = false,
  sampleName,
  sampleContent,
  sampleLabel = 'Download template',
  readContent = false,
  onFile,
  onError,
}) {
  const [file, setFile] = useState(null)

  const handle = useCallback(
    (f) => {
      if (!f) return
      if (maxMB && f.size > maxMB * 1048576) {
        if (onError) onError(`File exceeds ${maxMB} MB limit.`, f)
        return
      }
      setFile(f)
      if (readContent) {
        const rd = new FileReader()
        rd.onload = () => onFile && onFile(f, String(rd.result || ''))
        rd.onerror = () => {
          if (onError) onError('Could not read the file.', f)
        }
        rd.readAsText(f)
      } else if (onFile) {
        onFile(f)
      }
    },
    [maxMB, onError, onFile, readContent]
  )

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    multiple,
    onDrop: (accepted) => handle(accepted[0]),
  })

  const hintText = hint || `Accepted: ${accept}${maxMB ? ` · max ${maxMB} MB` : ''}`

  return (
    <div>
      <div {...getRootProps({className: cx('filedrop', isDragActive && 'drag')})}>
        <input {...getInputProps({accept})} />
        <div className='ic'>
          <CloudUploadOutlinedIcon fontSize='small' />
        </div>
        <div className='t'>{title}</div>
        <div className='s'>{hintText}</div>
      </div>
      {sampleName ? (
        <div style={{marginTop: 10, display: 'flex', gap: 10, alignItems: 'center'}}>
          <button
            type='button'
            className='btn btn-sm'
            onClick={() => download(sampleName, sampleContent || '')}
          >
            <DownloadOutlinedIcon style={{width: 15, height: 15}} />
            {sampleLabel}
          </button>
          <span className='hlp'>Uploaded files are saved automatically.</span>
        </div>
      ) : null}
      {file ? (
        <div className='fileitem'>
          <span className='ic' style={{alignSelf: 'flex-start', marginTop: 1}}>
            <InsertDriveFileOutlinedIcon fontSize='small' />
          </span>
          <div style={{flex: 1}}>
            <div style={{fontWeight: 600}}>{file.name}</div>
            <div className='hlp'>{(file.size / 1024).toFixed(1)} KB · ready</div>
          </div>
          <Badge status='uploaded' label='Selected' />
        </div>
      ) : null}
    </div>
  )
}

FileDrop.propTypes = {
  accept: PropTypes.string,
  title: PropTypes.string,
  hint: PropTypes.string,
  maxMB: PropTypes.number,
  multiple: PropTypes.bool,
  sampleName: PropTypes.string,
  sampleContent: PropTypes.string,
  sampleLabel: PropTypes.string,
  readContent: PropTypes.bool,
  onFile: PropTypes.func,
  onError: PropTypes.func,
}
